import { LegalLayout } from "./LegalLayout";

/**
 * DSGVO-Datenschutzerklärung. Public route at /datenschutz. The "Stand"
 * date is hand-rolled — bump it manually when this file changes. Don't
 * derive it from BUILD_DATE: every cosmetic redeploy would bump the date
 * and suggest a real privacy-policy change to the user.
 */
export function PrivacyPage() {
  return (
    <LegalLayout title="Datenschutzerklärung" subtitle="Stand: Mai 2026">
      <section>
        <h2 className="text-base font-semibold text-fg mb-1">1. Verantwortlicher</h2>
        <address className="not-italic">
          Concept4IT Consulting GmbH
          <br />
          Gustav-Freytag-Straße 36
          <br />
          60320 Frankfurt am Main
          <br />
          Deutschland
          <br />
          E-Mail: <span className="font-mono">contact[at]concept4it.com</span>
          <br />
          Vertreten durch: Doron Marcu, Geschäftsführer
        </address>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">2. Allgemeines</h2>
        <p>
          Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung der
          App-Funktion erforderlich ist, und nur auf Grundlage der DSGVO (Art. 6 lit. b
          und f).
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">3. Welche Daten erheben wir?</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>E-Mail-Adresse</strong> — eindeutige Konto-Kennung.
          </li>
          <li>
            <strong>Passwort</strong> — gespeichert als Argon2id-Hash; das Klartext-Passwort
            verlässt niemals deinen Browser.
          </li>
          <li>
            <strong>Einladungscode</strong> — Bezug zur Onboarding-Quelle.
          </li>
          <li>
            <strong>Lernfortschritt &amp; Antwort-Verlauf</strong> — welche Vokabel wann
            richtig/falsch beantwortet wurde, für die Spaced-Repetition-Logik.
          </li>
          <li>
            <strong>Einstellungen</strong> — Theme, Ton.
          </li>
          <li>
            <strong>Session-Cookie</strong> — httpOnly, SameSite=Lax, 30 Tage rolling.
          </li>
          <li>
            <strong>Letzte Anmeldung</strong> — Zeitpunkt der letzten erfolgreichen
            Authentifizierung.
          </li>
          <li>
            <strong>IP-Adresse &amp; User-Agent</strong> — nur transient in Server-Logs
            (Missbrauchserkennung), maximal 14 Tage Aufbewahrung.
          </li>
        </ul>
        <p className="mt-3">
          <strong>Technisch notwendige Cookies:</strong> Wir setzen ausschließlich das
          Session-Cookie. Es findet <strong>kein Tracking, keine Profilbildung, keine
          Werbe-Cookies</strong>. Deshalb ist eine Einwilligung nach § 25 Abs. 1 TTDSG
          nicht erforderlich.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">4. Zweck der Verarbeitung</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Bereitstellung der App-Funktion (Vokabel-Lernen mit persistentem Fortschritt).</li>
          <li>Cross-Device-Synchronisation deines Fortschritts.</li>
          <li>Passwort-Reset per E-Mail.</li>
          <li>Schutz gegen Brute-Force + Spam (Rate-Limiting).</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">5. Rechtsgrundlage</h2>
        <p>
          Art. 6 Abs. 1 lit. b DSGVO (Vertragsdurchführung) für die Nutzung des
          Vokabeltrainers.
          <br />
          Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse) für die Sicherheits-Logs.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">6. Speicherdauer</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Konto-Daten &amp; Fortschritt: bis du dein Konto löschst (Funktion „Konto löschen"
            im Menü).
          </li>
          <li>Server-Logs: 14 Tage.</li>
          <li>
            Soft-deleted Konten: Email anonymisiert, Passwort gelöscht; Hülle bleibt für
            den Audit-Trail.
          </li>
          <li>Session: 30 Tage rolling.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">7. Empfänger</h2>
        <p>
          Hosting + SMTP läuft bei <strong>Hostinger International Limitada</strong>
          (Server-Standort Frankfurt, Deutschland). Auftragsverarbeitungsvertrag (AVV)
          liegt vor. Keine sonstigen Drittübermittlungen.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">8. Deine Rechte</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Auskunft (Art. 15 DSGVO)</li>
          <li>Berichtigung (Art. 16)</li>
          <li>
            <strong>Löschung (Art. 17)</strong> — direkt in der App über „Konto löschen" im
            Menü ausführbar.
          </li>
          <li>Einschränkung der Verarbeitung (Art. 18)</li>
          <li>Datenübertragbarkeit (Art. 20)</li>
          <li>Widerspruch (Art. 21)</li>
        </ul>
        <p className="mt-3">
          Anfragen an: <span className="font-mono">contact[at]concept4it.com</span>
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">9. Beschwerderecht</h2>
        <p>
          Für uns zuständige Aufsichtsbehörde:
          <br />
          Der Hessische Beauftragte für Datenschutz und Informationsfreiheit
          <br />
          Postfach 3163, 65021 Wiesbaden
          <br />
          <a
            href="https://datenschutz.hessen.de"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline break-all"
          >
            https://datenschutz.hessen.de
          </a>
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-fg mb-1">10. Änderungen</h2>
        <p>
          Wir behalten uns vor, diese Erklärung anzupassen, sofern sich rechtliche
          Anforderungen ändern. Das Datum oben gibt den aktuellen Stand an.
        </p>
      </section>
    </LegalLayout>
  );
}
